import { getBasePath } from 'bc-artifacts-client/utils';
import { isTestEnv, propsToLogStrings, runAndLogError } from '../utils';
import type { ClientEnvironment } from 'bc-model';
import type { MetricsAdapter } from '../Metrics';
import type { Faro, MetaUser } from '@grafana/faro-web-sdk';
import { LogLevel } from '@grafana/faro-web-sdk';
import { getCategoryForMetricName } from '../getCategoryForMetricName';

const IGNORED_URLS = [
  // Match /alloy exactly, with optional trailing slash, query, or hash
  /\/alloy(?:\/)?(?:[?#].*)?$/,
  // Match any request to browser-intake-datadoghq.com (any protocol, subdomain, path)
  /(^https?:\/\/)?([a-z0-9.-]+\.)?browser-intake-datadoghq\.com\b/i,
  // Match any request to s3-accelerate.amazonaws.com
  /(^https?:\/\/)?([a-z0-9.-]+\.)?s3-accelerate\.amazonaws\.com\b/i,
  // Match any path containing /grafana/public/
  /\/grafana\/public\//,
  // Match /admin or anything under it, with optional query/hash
  /\/admin(?:\/.*)?(?:[?#].*)?$/,
  // Ignore common static assets
  /\.(?:png|jpe?g|webp|svg|ico|css|map|woff2?)(?:[?#].*)?$/i,
];

/**
 * This wraps getBasePath() for tests because jsdom/undici breaks Faro’s fetch when the base
 * path is '' or '/'. So, in tests, return a fake absolute URL instead.
 */
const getFaroBaseUrl = (): string => {
  const basePath = getBasePath();
  if (isTestEnv && (basePath === '' || basePath === '/')) {
    return 'http://fake-base-path.local';
  }
  return basePath;
};

const noOpFaroAdapter = (
  adapterContext: 'FaroAdapter' | 'WorkerFaroAdapter' = 'FaroAdapter'
): MetricsAdapter => {
  const notInitialized = `[${adapterContext}] Faro is not initialized yet.`;
  return {
    /* eslint-disable no-console */
    logEvent: () => {
      console.warn(`${notInitialized} Event not logged.`);
    },
    logWarning: () => {
      console.warn(`${notInitialized} Warning not logged.`);
    },
    logError: () => {
      console.warn(`${notInitialized} Error not logged.`);
    },
    logDuration: () => () => {
      console.warn(`${notInitialized} Duration not logged.`);
    },
    setUser: () => {
      console.warn(`${notInitialized} User not set.`);
    },
    setViewName: () => {
      console.warn(`${notInitialized} View name not set.`);
    },
    /* eslint-disable no-console */
  };
};

const faroAdapterFactory = (
  faro: Faro | null,
  adapterContext: 'FaroAdapter' | 'WorkerFaroAdapter' = 'FaroAdapter'
): MetricsAdapter => {
  if (!faro?.api) {
    // getFaro can return null if faro is disabled via environmental variable.
    // Lack of metric reporting doesn't break the app, (it still returns no-op functions
    // like setUser, logEvent, etc), but we should log it.
    console.warn(
      `[${adapterContext}] Faro is not available yet. Metrics will not be reported until it is initialized.`
    );
    return noOpFaroAdapter(adapterContext);
  }

  const faroApi = faro.api;
  return {
    setUser: (user) => {
      if (!user) return;
      const userConfig: MetaUser = {
        id: String(user.id),
        attributes: propsToLogStrings(user),
      };
      function setUser() {
        faroApi.setUser(userConfig);
        // using setUser doesn't update the user in the faro config.
        if (faro?.config) faro.config.user = userConfig;
      }
      runAndLogError(setUser);
    },
    setViewName: (viewName) => {
      if (!viewName) return;
      function setViewName() {
        faroApi.setPage(viewName);
      }
      runAndLogError(setViewName);
    },
    logDuration: (name, options = {}) => {
      const context = propsToLogStrings({
        ...options.context,
        description: options.description ?? '',
      });
      return (start, duration) => {
        function logDuration() {
          faroApi.pushMeasurement(
            {
              type: name,
              values: {
                start,
                duration,
              },
            },
            { context }
          );
        }
        runAndLogError(logDuration);
      };
    },
    logEvent: (data) => {
      const { name, ...context } = data;
      const category = getCategoryForMetricName(name);
      const sessionKey = faro?.config?.user?.attributes?.sessionKey;
      const eventAttributes = propsToLogStrings({
        ...context,
        sessionKey,
        category,
      });
      function logEvent() {
        faroApi.pushEvent(name, eventAttributes);
      }
      runAndLogError(logEvent);
    },
    logWarning: (warning, options = {}) => {
      const context = propsToLogStrings(options);
      function logWarning() {
        faroApi.pushLog([warning], {
          level: LogLevel.WARN,
          context,
        });
      }
      runAndLogError(logWarning);
    },
    logError: (error, options = {}) => {
      const context = propsToLogStrings(options);
      function logError() {
        faroApi.pushError(error, { context });
      }
      runAndLogError(logError);
    },
  };
};

type FaroEnvironmentVars = Partial<
  Pick<
    ClientEnvironment,
    'faroEnabled' | 'version' | 'faroPerfInstrumentationEnabled' | 'faroTracingEnabled'
  >
>;

export { IGNORED_URLS, getFaroBaseUrl, FaroEnvironmentVars, faroAdapterFactory };
